import { memo, type CSSProperties, type ReactNode } from 'react';
import type { ProfileEffectId } from '@clanfire/shared';

/** Stable pseudo-random numbers so particles don't jump around between renders. */
const rand = (i: number, salt: number) => {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

interface Particle {
  style: CSSProperties;
  content?: ReactNode;
}

function particles(id: ProfileEffectId): Particle[] {
  const make = (count: number, fn: (i: number, r: (salt: number) => number) => Particle) => Array.from({ length: count }, (_, i) => fn(i, (salt) => rand(i, salt)));
  switch (id) {
    case 'embers':
      return make(22, (i, r) => {
        const size = 2 + r(1) * 3.5;
        return {
          style: {
            left: `${r(2) * 100}%`,
            bottom: -8,
            width: size,
            height: size,
            borderRadius: '50%',
            background: r(3) > 0.5 ? '#ffb347' : '#ff6a3d',
            boxShadow: `0 0 ${size * 2}px ${r(3) > 0.5 ? '#ffb347' : '#ff6a3d'}`,
            animationName: 'hx-rise',
            animationDuration: `${4 + r(4) * 5}s`,
            animationDelay: `${-r(5) * 9}s`,
            ['--hx-drift' as string]: `${(r(6) - 0.5) * 60}px`,
          },
        };
      });
    case 'snow':
      return make(28, (i, r) => {
        const size = 2 + r(1) * 4;
        return {
          style: {
            left: `${r(2) * 100}%`,
            top: -8,
            width: size,
            height: size,
            borderRadius: '50%',
            background: '#fff',
            ['--hx-o' as string]: String(0.5 + r(3) * 0.5),
            animationName: 'hx-fall',
            animationDuration: `${6 + r(4) * 7}s`,
            animationDelay: `${-r(5) * 13}s`,
            ['--hx-drift' as string]: `${(r(6) - 0.5) * 50}px`,
          },
        };
      });
    case 'sparkles':
      return make(16, (i, r) => ({
        style: {
          left: `${r(1) * 94}%`,
          top: `${r(2) * 94}%`,
          fontSize: 8 + r(3) * 10,
          color: r(4) > 0.6 ? '#fde68a' : '#ffffff',
          textShadow: '0 0 6px rgba(255,255,255,0.8)',
          animationName: 'hx-twinkle',
          animationTimingFunction: 'ease-in-out',
          animationDuration: `${1.6 + r(5) * 2.4}s`,
          animationDelay: `${-r(6) * 4}s`,
        },
        content: '✦',
      }));
    case 'confetti':
      return make(26, (i, r) => ({
        style: {
          left: `${r(1) * 100}%`,
          top: -10,
          width: 5,
          height: 9,
          borderRadius: 1,
          background: ['#f43f5e', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ec4899'][i % 6],
          animationName: 'hx-fall',
          animationDuration: `${3.5 + r(2) * 4}s`,
          animationDelay: `${-r(3) * 7}s`,
          ['--hx-drift' as string]: `${(r(4) - 0.5) * 80}px`,
          ['--hx-spin' as string]: `${(r(5) - 0.5) * 1080}deg`,
        },
      }));
    case 'hearts':
      return make(14, (i, r) => ({
        style: {
          left: `${r(1) * 92}%`,
          bottom: -16,
          fontSize: 10 + r(2) * 10,
          color: r(3) > 0.5 ? '#fb7185' : '#f472b6',
          ['--hx-o' as string]: '0.85',
          animationName: 'hx-rise',
          animationDuration: `${5 + r(4) * 5}s`,
          animationDelay: `${-r(5) * 10}s`,
          ['--hx-drift' as string]: `${(r(6) - 0.5) * 40}px`,
        },
        content: '❤',
      }));
    case 'bubbles':
      return make(16, (i, r) => {
        const size = 6 + r(1) * 14;
        return {
          style: {
            left: `${r(2) * 96}%`,
            bottom: -24,
            width: size,
            height: size,
            borderRadius: '50%',
            border: '1.5px solid rgba(255,255,255,0.7)',
            background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.55), rgba(255,255,255,0.05) 60%)',
            ['--hx-o' as string]: '0.9',
            animationName: 'hx-rise',
            animationDuration: `${6 + r(3) * 6}s`,
            animationDelay: `${-r(4) * 12}s`,
            ['--hx-drift' as string]: `${(r(5) - 0.5) * 50}px`,
          },
        };
      });
    case 'rain':
      return make(34, (i, r) => ({
        style: {
          left: `${r(1) * 110 - 5}%`,
          top: -20,
          width: 1.5,
          height: 12 + r(2) * 10,
          background: 'linear-gradient(to bottom, transparent, rgba(186,230,253,0.85))',
          ['--hx-o' as string]: '0.8',
          ['--hx-drift' as string]: '-40px',
          animationName: 'hx-fall',
          animationDuration: `${0.9 + r(3) * 0.7}s`,
          animationDelay: `${-r(4) * 2}s`,
        },
      }));
    case 'aurora':
      return [
        { style: { left: '-20%', top: '-30%', width: '90%', height: '70%', borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(52,211,153,0.55), transparent)', filter: 'blur(18px)', animationName: 'hx-aurora', animationTimingFunction: 'ease-in-out', animationDuration: '9s' } },
        { style: { right: '-25%', top: '-20%', width: '90%', height: '65%', borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(129,140,248,0.5), transparent)', filter: 'blur(20px)', animationName: 'hx-aurora', animationTimingFunction: 'ease-in-out', animationDuration: '12s', animationDelay: '-4s' } },
        { style: { left: '10%', top: '5%', width: '70%', height: '45%', borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(244,114,182,0.35), transparent)', filter: 'blur(22px)', animationName: 'hx-aurora', animationTimingFunction: 'ease-in-out', animationDuration: '15s', animationDelay: '-8s' } },
      ];
    case 'wildfire':
      // Blaze: a fire burning along the bottom, throwing up sparks.
      return [
        {
          style: {
            left: '-10%',
            bottom: '-12%',
            width: '120%',
            height: '45%',
            background: 'radial-gradient(ellipse at 50% 100%, rgba(255,120,30,0.65), rgba(225,29,72,0.25) 55%, transparent 75%)',
            filter: 'blur(6px)',
            animationName: 'hx-pulse',
            animationTimingFunction: 'ease-in-out',
            animationDuration: '2.2s',
          },
        },
        ...make(16, (i, r) => ({
          style: {
            left: `${r(1) * 100 - 6}%`,
            bottom: -18,
            width: 12 + r(2) * 14,
            height: 26 + r(3) * 26,
            borderRadius: '50% 50% 45% 45% / 65% 65% 35% 35%',
            background: `linear-gradient(to top, ${r(4) > 0.5 ? '#ff3d00' : '#e11d48'}, #ff9100 55%, rgba(255,213,79,0.9))`,
            filter: 'blur(1.5px)',
            transformOrigin: '50% 100%',
            ['--hx-o' as string]: '0.85',
            animationName: 'hx-lick',
            animationTimingFunction: 'ease-in-out',
            animationDuration: `${0.8 + r(5) * 0.9}s`,
            animationDelay: `${-r(6) * 2}s`,
          },
        })),
        ...make(26, (i, r) => {
          const size = 1.5 + r(1) * 3;
          const color = ['#fff3b0', '#ffb347', '#ff6a3d'][i % 3]!;
          return {
            style: {
              left: `${r(2) * 100}%`,
              bottom: '12%',
              width: size,
              height: size,
              borderRadius: '50%',
              background: color,
              boxShadow: `0 0 ${size * 2.5}px ${color}`,
              animationName: 'hx-rise',
              animationDuration: `${2 + r(3) * 3}s`,
              animationDelay: `${-r(4) * 5}s`,
              ['--hx-drift' as string]: `${(r(5) - 0.5) * 90}px`,
            },
          };
        }),
      ];
    case 'bats':
      // Halloween: little bats flitting across the card, wings flapping, with a warm glow so they show on any card.
      return make(9, (i, r) => {
        const width = 14 + r(1) * 12;
        return {
          style: {
            left: '-14%',
            top: `${6 + r(2) * 70}%`,
            width,
            filter: 'drop-shadow(0 0 3px rgba(251,146,60,0.7))',
            animationName: 'hx-flit',
            animationTimingFunction: 'ease-in-out',
            animationDuration: `${7 + r(3) * 6}s`,
            animationDelay: `${-r(4) * 13}s`,
          },
          content: (
            <svg viewBox="0 0 24 12" className="hx-flap block" style={{ animationDuration: `${0.28 + r(5) * 0.14}s` }}>
              <path
                d="M12 3C11 1.5 10.5 1.5 10 2.6C8.5 1 6 .5 3.5 1.5 5 2.5 5 4 4.5 5 3.5 4.5 2 4.6.5 5.5 3 6 5 7.5 6 9.5 7 8.5 8.5 8.2 9.5 8.8 10 10 11 11 12 11.5 13 11 14 10 14.5 8.8 15.5 8.2 17 8.5 18 9.5 19 7.5 21 6 23.5 5.5 22 4.6 20.5 4.5 19.5 5 19 4 19 2.5 20.5 1.5 18 .5 15.5 1 14 2.6 13.5 1.5 13 1.5 12 3Z"
                fill="#2a1d36"
              />
            </svg>
          ),
        };
      });
    case 'fireflies':
      // Blaze: little lights drifting and blinking on a summer night.
      return make(20, (i, r) => {
        const size = 3 + r(1) * 3;
        return {
          style: {
            left: `${4 + r(2) * 90}%`,
            top: `${8 + r(3) * 84}%`,
            width: size,
            height: size,
            borderRadius: '50%',
            background: '#f7ff9e',
            boxShadow: `0 0 ${size * 2}px #d9f99d, 0 0 ${size * 4}px rgba(190,242,100,0.6)`,
            ['--hx-dx' as string]: `${(r(4) - 0.5) * 60}px`,
            ['--hx-dy' as string]: `${(r(5) - 0.5) * 40}px`,
            animationName: 'hx-firefly',
            animationTimingFunction: 'ease-in-out',
            animationDuration: `${5 + r(6) * 6}s`,
            animationDelay: `${-r(7) * 11}s`,
          },
        };
      });
    default:
      return [];
  }
}

/** Decorative animated layer covering a profile card (drawn above it, never blocking clicks). */
export const ProfileEffect = memo(function ProfileEffect({ id, className }: { id: ProfileEffectId; className?: string }) {
  return (
    <div aria-hidden className={`hx-anim hx-fx pointer-events-none absolute inset-0 overflow-hidden ${className ?? ''}`}>
      {particles(id).map((p, i) => (
        <span key={i} className="hx-p" style={p.style}>
          {p.content}
        </span>
      ))}
    </div>
  );
});
